/* eslint-disable no-console */

'use client';

/*
Corporate membership — mise en page alignée sur la homepage v5 : fond blanc,
titres Unbounded, comparatif à filets, engagements numérotés, formulaire sobre.
*/
import { useState } from 'react';
import ORImage from 'next/image';
import emailjs from 'emailjs-com';
import Head from '@/core/head';
import { Markdown, lang, hocT } from '@/helpers/translate';
import { getCanonicalUrl } from '@/helpers/router.js';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import {
  SECTION, CARD, BTN_PRIMARY, H1, H2, Kicker, ArrowIcon, NumberedList, kicker,
} from '@/core/editorial';

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '', companyName: '', role: '', message: '',
};

const FORM_LABELS = {
  fr: {
 firstName: 'Prénom', lastName: 'Nom', email: 'Email', phone: 'Téléphone', companyName: 'Entreprise', role: 'Fonction', message: 'Message', submit: 'Envoyer ma demande',
},
  en: {
 firstName: 'First name', lastName: 'Last name', email: 'Email', phone: 'Phone', companyName: 'Company', role: 'Role', message: 'Message', submit: 'Send my request',
},
  es: {
 firstName: 'Nombre', lastName: 'Apellido', email: 'Email', phone: 'Teléfono', companyName: 'Empresa', role: 'Cargo', message: 'Mensaje', submit: 'Enviar mi solicitud',
},
};

const FIELD = 'w-full rounded-lg border border-was-ink/12 bg-white px-4 py-3 text-[15px] text-was-ink outline-none transition-colors placeholder:text-was-muted focus:border-was-ink';

export default hocT(({ t }) => {
  const langCurrent = lang();
  const canurls = getCanonicalUrl('corporate-membership', langCurrent);
  const labels = FORM_LABELS[langCurrent] || FORM_LABELS.en;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState('');
  const [validMessage, setValidMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'companyName'];
    if (requiredFields.some((field) => !formData[field])) {
      setErrorMessage(t('workstars:form_empty'));
      return;
    }
    emailjs.sendForm('service_ch4rlms', 'template_g3dgl39', e.target, '9G_OX6KWnbY6dTSLl')
      .then(() => {
        setValidMessage(t('workstars:form_valid'));
        setFormData(EMPTY_FORM);
        setErrorMessage('');
      })
      .catch(() => {
        setErrorMessage(t('workstars:form_fail'));
      });
  };

  const parts = ['part1', 'part2'].map((p) => ({
    title: t(`workstars:zone1.${p}.title`),
    items: [1, 2, 3].map((i) => t(`workstars:zone1.${p}.text_${i}`)),
  }));
  const columns = [1, 2, 3, 4].map((i) => t(`workstars:zone2.col.title_${i}`));
  const lines = [1, 2, 3, 4].map((l) => [1, 2, 3, 4].map((i) => t(`workstars:zone2.line${l}.text_${i}`)));
  const practical = [1, 2, 3].map((i) => ({
    title: t(`workstars:zone3.part1.part${i}.title`),
    body: t(`workstars:zone3.part1.part${i}.text`),
  }));
  const commitments = [1, 2, 3].map((i) => ({
    title: t(`workstars:zone4.part${i}.title`),
    body: t(`workstars:zone4.part${i}.text`),
  }));

  return (
    <div className="bg-white font-display text-was-ink">
      <Head>
        {canurls.map((cprops) => <link key={JSON.stringify(cprops)} {...cprops} />)}
      </Head>

      {/* Hero */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-10 px-6 pb-16 pt-28 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8 lg:pb-24 lg:pt-36">
          <FadeUp>
            <Kicker n="01">{kicker(langCurrent, 'corpo', 0)}</Kicker>
            <h1 className={`mt-5 max-w-[640px] text-balance ${H1} [&_p]:m-0`}><Markdown>{t('workstars:h1_title')}</Markdown></h1>
            <p className="mt-6 max-w-[520px] text-lg leading-relaxed text-was-slate">{t('workstars:subtitle')}</p>
            <div className="mt-8">
              <a href="#workstars" className={BTN_PRIMARY}>
                {t('workstars:btn_1')}
                <ArrowIcon />
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className={`relative aspect-[4/5] overflow-hidden ${CARD} bg-was-cream`}>
            <ORImage alt="Corporate membership We Are Scene" className="object-cover" fill sizes="(max-width: 1024px) 100vw, 520px" src="/img/corpo-main2.webp" />
          </FadeUp>
        </div>
      </section>

      {/* Ce qui change */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="02">{kicker(langCurrent, 'corpo', 1)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('workstars:zone1.title')}</h2>
          </FadeUp>
          <Stagger stagger={0.1} delay={0.1} className="mt-12 grid gap-6 md:grid-cols-2 lg:mt-16 lg:gap-8">
            {parts.map((part, i) => (
              <StaggerChild key={part.title} className={`p-8 lg:p-10 ${CARD}`}>
                <span className="text-sm tabular-nums text-was-muted">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="m-0 mt-3 text-[20px] font-bold leading-tight tracking-[-0.02em]">{part.title}</h3>
                <ul className="m-0 mt-5 list-none space-y-3 border-t border-was-ink/8 p-0 pt-5 text-[15px] leading-relaxed text-was-slate">
                  {part.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-was-ink" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </StaggerChild>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Comparatif */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="03">{kicker(langCurrent, 'corpo', 2)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('workstars:zone2.title')}</h2>
            <p className="mt-5 max-w-[640px] text-base leading-relaxed text-was-slate">{t('workstars:zone2.text_1')}</p>
          </FadeUp>
          <div className="mt-12 overflow-x-auto lg:mt-16">
            <table className="w-full min-w-[720px] border-collapse text-left text-[15px]">
              <thead>
                <tr className="border-b border-was-ink/15">
                  {columns.map((c, i) => (
                    <th key={c} className={`py-4 pr-6 align-bottom font-medium text-was-ink ${i === 0 ? 'w-[34%] text-sm text-was-muted' : ''}`}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line[0]} className="border-b border-was-ink/8">
                    {line.map((cell, i) => (
                      <td key={`${line[0]}${cell}`} className={`py-5 pr-6 align-top leading-relaxed ${i === 0 ? 'font-medium text-was-ink' : 'text-was-slate'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* En pratique */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <div className="grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:gap-20">
            <div>
              <FadeUp>
                <Kicker n="04">{kicker(langCurrent, 'corpo', 3)}</Kicker>
                <h2 className={`mt-5 text-balance ${H2}`}>{t('workstars:zone3.part1.title')}</h2>
              </FadeUp>
              <div className="mt-10">
                <NumberedList items={practical} columns={1} />
              </div>
            </div>
            <FadeUp delay={0.1} className={`self-start p-8 lg:p-10 ${CARD}`}>
              <h3 className="m-0 text-[20px] font-bold leading-tight tracking-[-0.02em]">{t('workstars:zone3.part2.title')}</h3>
              <p className="m-0 mt-5 text-[15px] leading-relaxed text-was-slate">{t('workstars:zone3.part2.part1')}</p>
              <p className="m-0 mt-4 text-[15px] leading-relaxed text-was-slate">{t('workstars:zone3.part2.part2')}</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Engagements */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="05">{kicker(langCurrent, 'corpo', 4)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('workstars:zone4.title')}</h2>
          </FadeUp>
          <div className="mt-12 lg:mt-16">
            <NumberedList items={commitments} columns={3} />
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="workstars" className="scroll-mt-24 border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
            <FadeUp>
              <Kicker n="06">{kicker(langCurrent, 'corpo', 5)}</Kicker>
              <h2 className={`mt-5 text-balance ${H2}`}>{t('workstars:zone5.title')}</h2>
              <p className="mt-5 text-base leading-relaxed text-was-slate">{t('workstars:zone5.text')}</p>
            </FadeUp>
            <FadeUp delay={0.1} className={`p-8 lg:p-10 ${CARD}`}>
              <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2">
                {['firstName', 'lastName', 'email', 'phone', 'companyName', 'role'].map((name) => (
                  <label key={name} className="flex flex-col gap-1.5 text-[13px] text-was-muted">
                    {labels[name]}
                    <input
                      className={FIELD}
                      name={name}
                      type={name === 'email' ? 'email' : 'text'}
                      value={formData[name]}
                      onChange={handleChange}
                      required={name !== 'role'}
                    />
                  </label>
                ))}
                <label className="flex flex-col gap-1.5 text-[13px] text-was-muted sm:col-span-2">
                  {labels.message}
                  <textarea className={`${FIELD} min-h-[120px]`} name="message" value={formData.message} onChange={handleChange} />
                </label>
                <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                  <button type="submit" className={BTN_PRIMARY}>
                    {labels.submit}
                    <ArrowIcon />
                  </button>
                  {errorMessage && <span className="text-sm text-red-600">{errorMessage}</span>}
                  {validMessage && <span className="text-sm text-was-ink">{validMessage}</span>}
                </div>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
});
